/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import cn from "classnames";

import truncate from "utils/truncate";
import usePreviousRoute from "utils/usePreviousRoute";

import MoreContentDropDown from "components/common/MoreContentDropDown";
import { GoBackIcon } from "components/icons";
import { Button } from "components/ui/Button";

import Heading from "../Heading";
import Text from "../Text";

export interface IPageHeader {
  title: string;
  titleLeadingIconComponent?: React.ReactNode;
  note?: string;
  className?: string;
  showGoBack?: boolean;
  alwaysShowGoBack?: boolean;
  onCancel?: () => void;
  onGoBack?: () => void;
  children?: React.ReactNode | ((args: any) => React.ReactNode);
  titleClassName?: string;
  isChildrenFullWidth?: boolean;
  isStacked?: boolean;
  stackOnMobile?: boolean;
  isSticky?: boolean;
  slotClasses?: string;
  prevTitle?: string;
  prevLink?: string;
  midSection?: React.ReactNode;
}

const PageHeader = ({
  title,
  titleLeadingIconComponent,
  className,
  note,
  children,
  onCancel,
  onGoBack,
  showGoBack = true,
  alwaysShowGoBack = false,
  titleClassName = "w-40",
  isChildrenFullWidth = false,
  isStacked = false,
  slotClasses,
  prevTitle,
  prevLink,
  isSticky = true,
  midSection,
  stackOnMobile,
  ...rest
}: IPageHeader) => {
  const goBack = usePreviousRoute();
  return (
    <header
      className={cn(
        "flex justify-between items-center text-white py-5 gap-x-2",
        { "flex-col justify-start items-start gap-y-2": isStacked },
        stackOnMobile &&
          "max-sm:flex-col max-sm:justify-start max-sm:items-start max-sm:gap-y-4",
        { "sticky z-[40] top-[var(--spacing-60)] bg-c-gray-1000": isSticky },
        className,
      )}
      {...rest}
      test-id="vHrD7xxl"
    >
      <div test-id="LYE_XiE1">
        <div
          className={cn("flex", {
            "flex md:flex-col": prevTitle,
            "items-center gap-x-1 sm:gap-x-s0": !prevTitle,
            "gap-x-1 sm:!gap-x-s10": alwaysShowGoBack && !prevTitle,
          })}
          test-id="IADt6hZb"
        >
          <div
            className={cn({
              "sm:hidden": showGoBack && !alwaysShowGoBack && !prevTitle, // only show on mobile, hide on desktop
              hidden: !showGoBack && !alwaysShowGoBack && !prevTitle, // hide on mobile and desktop
            })}
            test-id="IlASHkh0"
          >
            <Button
              variant="unstyled"
              className={cn({
                "w-fit": prevTitle,
                "md:mt-1.5 mr-0": !prevTitle,
              })}
              onClick={
                onCancel
                  ? onCancel
                  : onGoBack
                    ? onGoBack
                    : () => goBack(prevLink)
              }
              test-id="3PWLvQP3"
            >
              <div
                className="text-[#9A9A9A] flex items-center gap-x-0.5 h-full w-max"
                test-id="j3TwqKBF"
              >
                <span
                  className={cn("flex items-center", {
                    "h-3 ": prevTitle,
                    "h-6": !prevTitle,
                  })}
                  test-id="8YuDBjwa"
                >
                  <GoBackIcon fill="#9A9A9A" test-id="wwUWqUV2" />
                </span>

                {prevTitle && (
                  <span className="w-max max-md:hidden" test-id="VRTUHfRT">
                    {prevTitle}
                  </span>
                )}
              </div>
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Heading
              as="h2"
              title={title}
              className={cn(
                "truncate sm:text-xl text-lg pl-2 sm:pl-1 max-xs:w-auto xs:min-w-fit",
                titleClassName,
                !titleClassName.includes("sm") && "sm:w-auto", // just for now...
              )}
              test-id="cfkk31sU"
            >
              {truncate(title || "", 30)}
            </Heading>

            {titleLeadingIconComponent ? titleLeadingIconComponent : null}
          </div>
        </div>

        {note && (
          <Text
            className="text-light-gray text-sm max-w-xl hidden lg:block"
            test-id="1UmXptx9"
          >
            {note}
          </Text>
        )}
      </div>

      {midSection && midSection}

      <div
        className={cn(
          "h-full flex items-start",
          {
            "w-full justify-end": isChildrenFullWidth,
          },
          slotClasses,
        )}
        test-id="5SRTrUdL"
      >
        {typeof children === "function"
          ? children(MoreContentDropDown)
          : children}
      </div>
    </header>
  );
};

export default PageHeader;
